import { useMemo } from "react";
import { Icon } from "./Icon";
import { Button } from "./ui";
import {
  normalizePdrProfile,
  PDR_PROFILE_OPTIONS,
  type PdrSchemaProfile,
} from "../lib/pdrFields";

type Props = {
  activeProfile: PdrSchemaProfile;
  onProfileChange: (profile: PdrSchemaProfile) => void;
  text: string;
  dirty: boolean;
  loading: boolean;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onTextChange: (text: string) => void;
  onSave: () => void;
  onInsertJsonSkeleton?: () => void;
  /** Optional: copy PDR markdown from a Phase 4 project (does not link future saves). */
  importProjectId: string;
  importProjectOptions: { project_id: string; label: string }[];
  onImportProjectChange: (projectId: string) => void;
  onImportFromProject: () => void;
  importLoading?: boolean;
};

function snippetForPreview(text: string, profile: PdrSchemaProfile): string {
  const flat = (text || "").replace(/\s+/g, " ").trim();
  if (!flat) {
    const label = PDR_PROFILE_OPTIONS.find((o) => o.id === profile)?.label || profile;
    return `No ${label} document`;
  }
  const heading = flat.match(/^#+\s+(.+)$/m)?.[1];
  if (heading) return heading.slice(0, 96);
  return flat.slice(0, 96);
}

export function EvalPdrSandbox({
  activeProfile,
  onProfileChange,
  text,
  dirty,
  loading,
  collapsed,
  onToggleCollapsed,
  onTextChange,
  onSave,
  onInsertJsonSkeleton,
  importProjectId,
  importProjectOptions,
  onImportProjectChange,
  onImportFromProject,
  importLoading,
}: Props) {
  const lineCount = useMemo(() => Math.max(1, text.split("\n").length), [text]);
  const snippet = snippetForPreview(text, activeProfile);
  const profileLabel =
    PDR_PROFILE_OPTIONS.find((o) => o.id === activeProfile)?.label || activeProfile;

  return (
    <section className="border-b border-outline-variant bg-surface flex flex-col shrink-0">
      <div className="px-6 py-3 flex items-center justify-between border-b border-outline-variant/50 gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1 flex-wrap">
          <span className="font-data-label text-data-label uppercase text-on-surface-variant shrink-0">
            Eval PDR (sandbox)
          </span>
          <div
            className="flex rounded border border-outline-variant overflow-hidden shrink-0"
            role="tablist"
            aria-label="PDR schema profile"
          >
            {PDR_PROFILE_OPTIONS.map((o) => (
              <button
                key={o.id}
                type="button"
                role="tab"
                aria-selected={activeProfile === o.id}
                onClick={() => onProfileChange(normalizePdrProfile(o.id))}
                className={`px-2.5 py-1 text-[10px] font-data-label uppercase tracking-wide transition-colors ${
                  activeProfile === o.id
                    ? "bg-primary/15 text-primary border-r border-outline-variant last:border-r-0"
                    : "text-on-surface-variant hover:bg-surface-container-high/60 border-r border-outline-variant last:border-r-0"
                }`}
              >
                {o.id === "slim" ? "Slim" : "Complete"}
              </button>
            ))}
          </div>
          <span
            className={`text-body-sm text-on-surface-variant truncate min-w-0 max-w-[min(100%,28rem)] ${
              collapsed ? "opacity-100" : "opacity-80"
            }`}
            title={snippet}
          >
            {snippet}
          </span>
          {dirty ? (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border uppercase shrink-0 bg-tertiary/10 text-tertiary border-tertiary/20">
              Unsaved
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleCollapsed}
            className="uppercase font-data-label text-data-label"
          >
            <Icon name={collapsed ? "expand_more" : "expand_less"} />
            {collapsed ? "Expand" : "Collapse"}
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={onSave}
            disabled={loading || !text.trim()}
            title={`Save ${profileLabel} eval PDR for future runs`}
          >
            Save PDR
          </Button>
        </div>
      </div>

      {!collapsed ? (
        <>
          <div className="px-6 py-2 border-b border-outline-variant/40 flex flex-wrap items-end gap-2 bg-surface-container-lowest/50">
            <label className="text-[10px] uppercase tracking-widest text-on-surface-variant font-data-label">
              Optional: import starting point from project
            </label>
            <select
              value={importProjectId}
              onChange={(e) => onImportProjectChange(e.target.value)}
              className="desk-input text-xs px-2 py-1.5 min-w-[200px] max-w-full font-data-mono"
              disabled={importLoading || importProjectOptions.length === 0}
            >
              <option value="">Choose project…</option>
              {importProjectOptions.map((p) => (
                <option key={p.project_id} value={p.project_id}>
                  {p.label}
                </option>
              ))}
            </select>
            <Button
              size="sm"
              onClick={onImportFromProject}
              disabled={!importProjectId || importLoading}
            >
              {importLoading ? "Loading…" : "Import into sandbox"}
            </Button>
            {onInsertJsonSkeleton ? (
              <Button
                size="sm"
                variant="outline-primary"
                onClick={onInsertJsonSkeleton}
                disabled={importLoading}
                title="Replace editor with empty JSON for every field in this profile (best for Complete runs)"
              >
                Insert JSON skeleton
              </Button>
            ) : null}
          </div>
          <div className="flex code-editor-gradient border-b border-outline-variant max-h-[min(52vh,520px)] min-h-[200px]">
            {loading ? (
              <p className="p-6 text-body-sm text-on-surface-variant w-full">Loading…</p>
            ) : (
              <>
                <pre className="py-3 px-2 text-right text-outline font-data-mono text-xs select-none w-10 shrink-0 overflow-hidden">
                  {Array.from({ length: lineCount }, (_, i) => i + 1).join("\n")}
                </pre>
                <textarea
                  key={activeProfile}
                  value={text}
                  onChange={(e) => onTextChange(e.target.value)}
                  className="flex-1 p-3 bg-transparent font-data-mono text-sm text-on-surface resize-none focus:outline-none min-h-[200px]"
                  spellCheck={false}
                  aria-label={`Eval sandbox PDR document (${profileLabel})`}
                  placeholder={`# Existing project state — ${profileLabel}&#10;&#10;Paste or write markdown / JSON. Stored separately from the other profile.`}
                />
              </>
            )}
          </div>
        </>
      ) : null}

      {!collapsed ? (
        <p className="px-6 py-2 text-[11px] text-on-surface-variant border-t border-outline-variant/50 leading-relaxed">
          Two independent documents: <strong className="text-on-surface/80">Slim</strong> and{" "}
          <strong className="text-on-surface/80">Complete</strong> are stored in workspace + browser.
          Runs use the active tab’s document and matching validation schema. Never written to Phase 3/4
          production projects.
        </p>
      ) : null}
    </section>
  );
}
